import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
    DataTable,
    EmptyState,
    ErrorState,
    SectionCard,
    SkeletonTable,
    type ColumnConfig,
} from '@/components/ui';
import type { AdminAuditRow } from '@/api/console';
import type { AppDetailPageVM } from './useAppDetailPage';

/**
 * EAA-1817 (frame 51): Nhật ký tab.
 *
 * Two known gaps vs the mock, recorded rather than papered over:
 * (1) `GET /admin/audit` has no `target_id` filter server-side, so app-scoping
 *     is client-side (same pattern `AccountAuditPanel`'s `lockedUsername`
 *     already uses) — fine at today's volume, a future BE ticket if it grows.
 * (2) `AdminAudit.ACTION_CHOICES` covers registry/client/CI-token/role-create
 *     writes only. Several rows the mock shows (catalog sync, per-org scope
 *     change, the enable/disable flip) are not written to ANY append-only
 *     audit table today — they exist only as `logger.info` calls. This tab
 *     can only ever show what portal-api actually audits.
 *
 * EAA-1884: hand-rolled `<table className="app-detail-audit-table">` → kit
 * `DataTable`, same conversion `AppsTable`/`AccountsTable` already went
 * through for this ticket family. No sort/filter/pagination existed on the
 * raw table (the query pulls one 100-row page and renders it whole, no
 * "load more" affordance), so this stays a plain read-only `DataTable` with
 * `pagination={false}` — a straight component swap, not a redesign.
 */
export function AppDetailAuditTab({ vm }: { vm: AppDetailPageVM }) {
    const { t } = useTranslation();

    const columns: ColumnConfig<AdminAuditRow>[] = useMemo(
        () => [
            {
                key: 'created_at',
                title: t('console.appDetail.audit.columnTime'),
                dataIndex: 'created_at',
                render: (value) => new Date(value as string).toLocaleString('vi-VN'),
            },
            {
                key: 'actor_label',
                title: t('console.appDetail.audit.columnActor'),
                dataIndex: 'actor_label',
                render: (value) => (value as string) || t('console.appDetail.audit.systemActor'),
            },
            {
                key: 'action',
                title: t('console.appDetail.audit.columnAction'),
                dataIndex: 'action',
                render: (value) =>
                    t(`console.appDetail.audit.action.${value as string}`, {
                        defaultValue: value as string,
                    }),
            },
            {
                key: 'success',
                title: t('console.appDetail.audit.columnResult'),
                dataIndex: 'success',
                render: (value) => (
                    <span
                        className={'app-detail-source-pill' + (value ? ' is-synced' : ' is-failed')}
                    >
                        {value
                            ? t('console.appDetail.audit.resultSuccess')
                            : t('console.appDetail.audit.resultFailure')}
                    </span>
                ),
            },
        ],
        [t]
    );

    if (!vm.canViewAudit) {
        return (
            <SectionCard>
                <p className="portal-admin-hint">{t('console.appDetail.audit.accessDenied')}</p>
            </SectionCard>
        );
    }

    return (
        <SectionCard bodyPadded={false}>
            {vm.auditQuery.isLoading && <SkeletonTable rows={8} columns={4} />}
            {vm.auditQuery.isError && (
                <ErrorState
                    description={t('console.appDetail.audit.loadError')}
                    onRetry={() => vm.auditQuery.refetch()}
                    retrying={vm.auditQuery.isFetching}
                />
            )}
            {vm.auditQuery.isSuccess && vm.auditRows.length === 0 && (
                <EmptyState
                    title={t('console.appDetail.audit.emptyTitle')}
                    description={t('console.appDetail.audit.emptyDescription')}
                />
            )}
            {vm.auditQuery.isSuccess && vm.auditRows.length > 0 && (
                <div className="portal-table-scroll">
                    <DataTable<AdminAuditRow>
                        data={vm.auditRows}
                        columns={columns}
                        rowKey="id"
                        pagination={false}
                    />
                </div>
            )}
        </SectionCard>
    );
}

export default AppDetailAuditTab;
